import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Warning, Clock, ShieldCheck, Storefront } from "@phosphor-icons/react";
import Header from "../components/Header";
import FeedbackCTA from "../components/FeedbackCTA";
import { RETAILER_LANDINGS } from "../lib/retailerLandingData";

/**
 * /retailers — public index of supported NZ retailers + their published
 * price-protection windows. Answers the first question every visitor has:
 * "which stores does this work at?"
 *
 * Honest by design: confirmed-policy retailers get a green badge, others
 * get an amber "no published window — we ask politely" badge. Never invents
 * a policy a retailer hasn't published.
 */
const ROADMAP = [
  { name: "Noel Leeming", reason: "Anti-bot blocking our scraper for now." },
  { name: "Mighty Ape", reason: "Anti-bot blocking our scraper for now." },
  { name: "The Warehouse", reason: "On the shortlist once a published price-protection window is confirmed." },
  { name: "Bunnings", reason: "Investigating their Lowest Price Guarantee for post-purchase fit." },
  { name: "Farmers", reason: "On the shortlist - watching for a public policy." },
  { name: "Jaycar", reason: "Specialist electronics chain. On deck for scraper work." },
];

export default function Retailers() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Supported retailers · PriceGuard NZ";
    return () => { document.title = prevTitle; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#0a0a0a]" data-testid="retailers-index-page">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-2xl">
          <div className="pg-overline mb-3">Supported retailers</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            Where PriceGuard<br/>works today<span className="text-[#002FA7]">.</span>
          </h1>
          <p className="text-[#52525b] text-base sm:text-lg leading-relaxed mt-6">
            Three major NZ retailers are live for daily price-protection tracking right now. Every supported retailer has a published or commonly-honoured window we watch on your behalf. We never invent a policy a retailer hasn't published.
          </p>
        </div>

        {/* LIVE RETAILERS GRID */}
        <section className="mt-12" data-testid="retailers-live-grid">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {Object.values(RETAILER_LANDINGS).map((r) => {
              const confirmed = r.policyConfidence === "confirmed";
              return (
                <Link
                  key={r.slug}
                  to={`/track/${r.slug}`}
                  className="bg-white p-7 flex flex-col gap-4 pg-lift group"
                  data-testid={`retailers-card-${r.slug}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="font-heading text-2xl font-extrabold tracking-tighter">{r.name}</div>
                    <span className="text-[10px] font-bold tracking-widest uppercase bg-[#16A34A] text-white px-2 py-1">Live</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    {confirmed ? <CheckCircle size={18} weight="fill" color="#16A34A" className="mt-0.5 flex-shrink-0" /> : <Warning size={18} weight="fill" color="#D97706" className="mt-0.5 flex-shrink-0" />}
                    <div className="text-sm leading-snug">
                      <div className="font-bold">
                        {confirmed ? `${r.policyDays}-day window` : `~${r.policyDays}-day default`}
                        {confirmed && r.policyName ? ` · ${r.policyName}` : ""}
                      </div>
                      <div className="text-[#52525b] mt-1 text-xs">
                        {confirmed ? "Published by the retailer." : "No published window. We ask politely on your behalf."}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-[#52525b] leading-relaxed line-clamp-3">
                    {r.summary}
                  </div>
                  <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-[#002FA7] group-hover:translate-x-0.5 transition-transform">
                    See how it works at {r.name} <ArrowRight size={14} weight="bold" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* HOW WINDOWS WORK */}
        <section className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="retailers-policy-explainer">
          <div className="lg:col-span-5">
            <div className="pg-overline mb-3">What's a price-protection window?</div>
            <h2 className="font-heading text-2xl lg:text-3xl font-extrabold tracking-tight leading-tight">
              The short window where you can ask for the difference back.
            </h2>
          </div>
          <div className="lg:col-span-7 text-[#0a0a0a] text-base leading-relaxed space-y-4">
            <p>
              Most NZ retailers will refund or credit the difference if a product you bought goes on sale within a short period of your purchase. The window length varies by retailer (and sometimes by product category).
            </p>
            <p>
              The catch is that almost nobody monitors prices day-by-day during that window. PriceGuard does it for you, then drafts a polite, retailer-ready refund email anchored in their own published policy.
            </p>
            <p className="text-sm text-[#52525b] border-l-2 border-[#e4e4e7] pl-4">
              The retailer's decision is always final - we never promise a guaranteed refund. We just make sure you don't miss the window.
            </p>
          </div>
        </section>

        {/* ROADMAP */}
        <section className="mt-16" data-testid="retailers-roadmap">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-8">
            <div className="lg:col-span-5">
              <div className="pg-overline mb-3">On the roadmap</div>
              <h2 className="font-heading text-2xl lg:text-3xl font-extrabold tracking-tight leading-tight">
                Coming soon, in order of likely arrival.
              </h2>
            </div>
            <div className="lg:col-span-7 text-[#52525b] text-base leading-relaxed">
              We add retailers when (1) they publish a price-protection window we can honour, and (2) we can read their product pages reliably without breaching their terms of service. Want to nominate one? Drop us an email at <a href="mailto:hello@priceguard.nz" className="underline hover:text-black">hello@priceguard.nz</a>.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {ROADMAP.map((r) => (
              <div key={r.name} className="bg-white p-6 flex flex-col gap-3" data-testid={`roadmap-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="font-heading text-lg font-bold tracking-tight">{r.name}</div>
                  <span className="text-[9px] font-bold tracking-widest uppercase bg-[#e4e4e7] text-[#52525b] px-1.5 py-0.5">Coming soon</span>
                </div>
                <div className="flex items-start gap-2 text-sm text-[#52525b] leading-relaxed">
                  <Clock size={14} weight="duotone" className="mt-0.5 flex-shrink-0" /> {r.reason}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* TRUST + CTA */}
        <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]" data-testid="retailers-trust-strip">
          <div className="bg-white p-6">
            <ShieldCheck size={22} weight="duotone" color="#002FA7" />
            <div className="font-heading font-bold tracking-tight mt-3">Read public pages only</div>
            <p className="text-sm text-[#52525b] mt-2 leading-relaxed">
              We respect robots.txt and only read public product pages. We never bypass login walls or scrape personal data.
            </p>
          </div>
          <div className="bg-white p-6">
            <Storefront size={22} weight="duotone" color="#002FA7" />
            <div className="font-heading font-bold tracking-tight mt-3">Retailer-respectful tone</div>
            <p className="text-sm text-[#52525b] mt-2 leading-relaxed">
              Our drafts are always polite and accurate. We cite the retailer's own policy by name where one is published, and ask politely where it isn't.
            </p>
          </div>
          <div className="bg-white p-6">
            <CheckCircle size={22} weight="duotone" color="#002FA7" />
            <div className="font-heading font-bold tracking-tight mt-3">No affiliation claimed</div>
            <p className="text-sm text-[#52525b] mt-2 leading-relaxed">
              PriceGuard is an independent service. We're not endorsed by, partnered with, or operating on behalf of any retailer named on this site.
            </p>
          </div>
        </section>

        <section className="mt-12 border border-[#0a0a0a] bg-[#0a0a0a] text-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="retailers-cta">
          <div>
            <div className="font-heading text-2xl font-extrabold tracking-tighter">Start watching your next purchase.</div>
            <div className="text-sm text-white/60 mt-2">Pro free for everyone right now. No card needed. Free forever for up to 5 items.</div>
          </div>
          <Link to="/signup" className="bg-white text-black font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#f0f0f2]" data-testid="retailers-signup-cta">
            Sign up <ArrowRight size={16} weight="bold" />
          </Link>
        </section>

        <div className="mt-12">
          <FeedbackCTA
            variant="card"
            prompt="Missing a retailer you'd like to see supported?"
            subject="Retailer request for PriceGuard NZ"
          />
        </div>
      </main>
    </div>
  );
}
